import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../i18n';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import runningIcon from '../../assets/icons/running.png';
import cyclingIcon from '../../assets/icons/cycling.png';
import swimIcon from '../../assets/icons/swim.png';
import strengthIcon from '../../assets/icons/strength.png';
import tzoneLogo from '../../assets/img/logo.png';

const activityIcons = {
  correr: runningIcon,
  ciclismo: cyclingIcon,
  nadar: swimIcon,
  fuerza: strengthIcon,
};

const safe = (n) => (typeof n === 'number' && isFinite(n) ? n : 0);
const toHours = (minutes) => safe(minutes) / 60;

// Si es nadar y parece metros, convertir a km
const toKmIfSwim = (type, value) => {
  let v = safe(value);
  if (type === 'nadar' && v >= 50) v = v / 1000;
  return v;
};


// Formatea el valor crudo de cada métrica para mostrarlo junto al % (planificado vs completado)
const formatMetricValue = (field, value) => {
  const v = safe(value);
  if (field === 'distance') return `${v.toFixed(1)} km`;
  if (field === 'time') {
    const h = Math.floor(v / 60);
    const m = Math.round(v % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }
  if (field === 'elevation') return `${Math.round(v)} m`;
  if (field === 'calories') return `${Math.round(v)} kcal`;
  return String(Math.round(v));
};
const order = ['correr', 'ciclismo', 'nadar', 'fuerza'];

const COLOR_PLANNED = am5.color('#f7941d');
const COLOR_DONE = am5.color('#193255');

export default function WeeklySummaryChart({
  weeklySummary,
  defaultMetric = 'distance',
  height = 360,
}) {
  const { t } = useTranslation();
  const [metric, setMetric] = useState(defaultMetric);
  const chartRef = useRef(null);
  const rootRef = useRef(null);

  const labelByMetric = useMemo(
    () => ({
      distance: t('weeklySummaryChart.metrics.distance'),
      time: t('weeklySummaryChart.metrics.time'),
      calories: t('weeklySummaryChart.metrics.calories'),
      elevation: t('weeklySummaryChart.metrics.elevation'),
      tss: t('weeklySummaryChart.metrics.tss'),
    }),
    [t]
  );

  const buttonLabelByMetric = useMemo(
    () => ({
      distance: t('weeklySummaryChart.short.distance'),
      time: t('weeklySummaryChart.short.time'),
      calories: t('weeklySummaryChart.short.calories'),
      elevation: t('weeklySummaryChart.short.elevation'),
      tss: t('weeklySummaryChart.short.tss'),
    }),
    [t]
  );

  const data = useMemo(() => {
    const byType = weeklySummary?.byType || {};
    return order.map((tpe) => {
      const plannedRaw = safe(byType[tpe]?.planned?.[metric]);
      const completedRaw = safe(byType[tpe]?.completed?.[metric]);

      let planned = plannedRaw;
      let completed = completedRaw;

      if (metric === 'time') {
        planned = toHours(plannedRaw);
        completed = toHours(completedRaw);
      }
      if (metric === 'distance') {
        planned = toKmIfSwim(tpe, planned);
        completed = toKmIfSwim(tpe, completed);
      }

      return {
        type: tpe,
        label: tpe[0].toUpperCase() + tpe.slice(1),
        planned,
        completed,
        _plannedRaw: plannedRaw,
        _completedRaw: completedRaw,
        icon: activityIcons[tpe] || tzoneLogo,
      };
    });
  }, [weeklySummary, metric]);

  const weekTotals = useMemo(() => {
    const byType = weeklySummary?.byType || {};
    const totalFor = (field) =>
      order.reduce((sum, type) => sum + safe(byType[type]?.completed?.[field]), 0);

    return ['distance', 'time', 'elevation', 'calories']
      .map((field) => ({
        key: field,
        label: buttonLabelByMetric[field] || field,
        display: formatMetricValue(field, totalFor(field)),
      }));
  }, [weeklySummary, buttonLabelByMetric]);

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    const root = am5.Root.new(chartRef.current);
    rootRef.current = root;
    root.setThemes([am5themes_Animated.new(root)]);
    if (root._logo) root._logo.dispose();

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        wheelX: 'none',
        wheelY: 'none',
        paddingRight: 20,
      })
    );

    // ===== Ejes
    const yRenderer = am5xy.AxisRendererY.new(root, {
      inversed: true,
      cellStartLocation: 0.15,
      cellEndLocation: 0.85,
      minGridDistance: 20,
    });

    yRenderer.labels.template.setAll({ visible: false });
    chart.leftAxesContainer.setAll({ paddingLeft: 64 });

    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'label',
        renderer: yRenderer,
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 40 }),
      })
    );

    // ===== Series
    const makeSeries = (name, field, color) => {
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name,
          xAxis,
          yAxis,
          valueXField: field,
          categoryYField: 'label',
          clustered: true,
          maskBullets: false,
          tooltip: am5.Tooltip.new(root, { pointerOrientation: 'horizontal' }),
        })
      );

      series.columns.template.setAll({
        height: am5.p100,
        cornerRadiusTL: 8,
        cornerRadiusBL: 8,
        fillOpacity: 0.95,
        strokeOpacity: 1,
        stroke: color,
        fill: color,
      });

      series.bullets.push(() =>
        am5.Bullet.new(root, {
          locationX: 1,
          sprite: am5.Label.new(root, {
            centerY: am5.p50,
            populateText: true,
            text:
              metric === 'time'
                ? '{valueX.formatNumber("#,##.##")}h ({_valueRaw.formatNumber("#,##")}m)'
                : '{valueX.formatNumber("#,##.##")}',
            fill: am5.color(0xffffff),
            dx: 6,
          }),
        })
      );
      return series;
    };

    const sPlanned = makeSeries(
      t('weeklySummaryChart.planned'),
      'planned',
      COLOR_PLANNED
    );
    const sCompleted = makeSeries(
      t('weeklySummaryChart.completed'),
      'completed',
      COLOR_DONE
    );

    // Data
    yAxis.data.setAll(data);
    sPlanned.data.setAll(
      data.map((r) => ({
        ...r,
        _valueRaw: metric === 'time' ? r._plannedRaw : r.planned,
      }))
    );
    sCompleted.data.setAll(
      data.map((r) => ({
        ...r,
        _valueRaw: metric === 'time' ? r._completedRaw : r.completed,
      }))
    );

    // Tooltips
    const tipText =
      metric === 'time'
        ? `[bold]{name}[/]\n{category}: {valueX.formatNumber("#,##.##")} ${t('weeklySummaryChart.units.hours')}\n({valueRaw} ${t('weeklySummaryChart.units.minutes')})`
        : '[bold]{name}[/]\n{category}: {valueX.formatNumber("#,##.##")}';

    [sPlanned, sCompleted].forEach((s) => {
      s.get('tooltip').label.set('text', tipText);
      s.adapters.add('tooltipDataItem', (di) => {
        if (di?.dataContext) {
          di.dataContext.valueRaw =
            s === sPlanned ? di.dataContext._plannedRaw : di.dataContext._completedRaw;
        }
        return di;
      });
    });

    // ===== Íconos en el eje Y
    const placeAxisBulletsOnce = () => {
      const items = yAxis.dataItems || [];
      items.forEach((di) => {
        if (di.getPrivate('tzoneIcon')) return;

        const cat = di.get('category') || '';
        const typeKey = cat.toLowerCase();
        const icon = activityIcons[typeKey] || tzoneLogo;

        const bullet = am5.Bullet.new(root, {
          location: 0.5,
          sprite: am5.Picture.new(root, {
            width: 22,
            height: 22,
            centerY: am5.p50,
            x: -28,
            zIndex: 1000,
            src: icon,
          }),
        });

        di.set('bullet', bullet);
        di.setPrivate('tzoneIcon', bullet);
      });
    };

    placeAxisBulletsOnce();
    yAxis.events.on('datavalidated', placeAxisBulletsOnce);

    sPlanned.appear(600);
    sCompleted.appear(800);
    chart.appear(600, 50);

    return () => root.dispose();
  }, [data, metric, labelByMetric, t]);

  useEffect(() => {}, [metric]);

  return (
    <div className="w-full">
      <div className="tz20-wsc-buttons">
        {(['distance', 'time', 'calories', 'elevation', 'tss']).map((m) => (
          <button
            key={m}
            onClick={() => setMetric(m)}
            className={m === metric ? 'active' : ''}
            title={labelByMetric[m]}
          >
            {buttonLabelByMetric[m]}
          </button>
        ))}
      </div>

      <div className="tz20-wsc-legend">
        <span><i className="planned" />{t('weeklySummaryChart.planned')}</span>
        <span><i className="done" />{t('weeklySummaryChart.completed')}</span>
      </div>

      <div
        ref={chartRef}
        style={{ width: '100%', height }}
        aria-label={t('weeklySummaryChart.ariaLabel')}
      />

      {weekTotals.length > 0 && (
        <div className="tz20-week-totals">
          {weekTotals.map((row) => (
            <div key={row.key} className="tz20-week-total-item">
              <span className="k">{row.label}</span>
              <span className="v">{row.display}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
